import {
  persistSingleMatches,
  singleMatchesAreServerAuthoritative,
  createSingleMatchCmd,
  patchSingleMatch,
  deleteSingleMatchCmd,
} from '../lib/api/matches';
import { persistPlayers, fetchPlayers } from '../lib/api/players';
import {
  createSingleMatch,
  calcSingleMatchEloChange,
  applySingleMatchElo,
} from '../engine/singleMatch';

// ─────────────────────────────────────────────────────────────────────
// useSingleMatchActions — the single-match (enkeltkamp) CRUD handlers lifted
// verbatim out of `Padel24AppMain` (extracted from the former monolith) as a
// god-component collapse slice (PR-AW; mirrors the useDrafts pattern).
//
// TWO PATHS, since #245. On the API backend each handler sends an INTENT and
// the SERVER computes the rating change and writes the match and the four
// players in one transaction — nothing below the guard runs. In memory
// the original persist-first path is unchanged (await persistSingleMatches +
// persistPlayers before setState), because that is what the deployed browser
// runs; it is also what the server path reproduces. ELO via the
// engine primitives — `Padel24Engine.singleMatch.create/calcEloChange/applyElo`
// are STATIC refs to
// createSingleMatch/calcSingleMatchEloChange/applySingleMatchElo, so importing
// them directly is behaviour-identical.
//
// The usePlayers store slices are passed in via `deps`.
//
// `liveAllPlayers` USED TO BE ONE OF THEM and is not any more (#242). The add
// path calculated against it and applied against the stored list, so a club
// with a tournament running recorded a change its players never received. One
// calculation now, against the stored list, and the live view is not needed
// here at all.
// ─────────────────────────────────────────────────────────────────────
export interface UseSingleMatchActionsDeps {
  allPlayers: any;
  setAllPlayers: (v: any) => void;
  singleMatches: any;
  setSingleMatches: (v: any) => void;
}

export function useSingleMatchActions({
  allPlayers,
  setAllPlayers,
  singleMatches,
  setSingleMatches,
}: UseSingleMatchActionsDeps) {
  const addSingleMatch = async ({
    team1,
    team2,
    sets,
    format,
    deuceMode,
    kFactor,
    eloWeightPct,
    playedAt,
  }: any) => {
    console.log('[Padel24] addSingleMatch mottok:', {
      team1,
      team2,
      sets,
      format,
      eloWeightPct,
      playedAt,
    });

    // ── SERVER-AUTHORITATIVE PATH (#245) ─────────────────────────────
    //
    // The result travels as an INTENT; the server runs the same engine calls
    // and writes the match and the four ratings in ONE transaction. Nothing is
    // computed here, because two places computing a rating is how they come to
    // disagree.
    if (singleMatchesAreServerAuthoritative()) {
      try {
        const { match: created, calc } = await createSingleMatchCmd({
          team1,
          team2,
          sets,
          format,
          deuceMode,
          kFactor,
          eloWeightPct,
          playedAt,
        });
        // The server moved the ratings, so the local copy is stale. Re-read
        // rather than patching it from the response: the reply names the match,
        // and the player list is the server's answer to what the ratings now
        // are.
        const freshPlayers = await fetchPlayers();
        setSingleMatches([created, ...singleMatches]);
        setAllPlayers(freshPlayers);
        return { success: true, match: created, calc };
      } catch (e: any) {
        console.error('[Padel24] Kunne ikke lagre enkeltkamp:', e);
        return { success: false, error: e.message };
      }
    }

    // Create the match with the computed ELO change
    const match = createSingleMatch({
      team1,
      team2,
      sets,
      format,
      deuceMode,
      kFactor,
      eloWeightPct,
      playedAt,
      registeredBy: 'admin',
    });
    console.log('[Padel24] match opprettet:', match);

    // ONE CALCULATION, recorded AND applied (#242).
    //
    // This used to measure against `liveAllPlayers` — the stored list plus the
    // uncommitted deltas of active and paused tournaments — while
    // `applySingleMatchElo` below recalculated internally against the stored
    // list. So a club with a tournament running recorded a match whose
    // displayed change was not the change its players received: 19 shown, 20
    // applied, on the fixture that pinned it.
    //
    // The stored list is what both use now. It is the player's actual
    // authoritative rating — and since #404/#406 it already includes every
    // padel24 and americano result, committed as it was recorded. Calculating
    // against the live view instead would also bank an in-progress
    // klubbmesterskap's or seriespill's deltas early, since those still commit
    // at archive.
    const calc = calcSingleMatchEloChange(allPlayers, match);
    console.log('[Padel24] ELO-beregning:', calc);
    if (calc.error) {
      console.error('[Padel24] ELO-beregning feilet:', calc.error);
      return { success: false, error: calc.error };
    }

    // Enrich the match with ELO history (before/after)
    const enrichedMatch = {
      ...match,
      result: {
        winner: calc.winner,
        t1Games: calc.t1Games,
        t2Games: calc.t2Games,
        t1EloBefore: calc.t1EloBefore,
        t2EloBefore: calc.t2EloBefore,
        t1Change: calc.t1Change,
        t2Change: calc.t2Change,
        mov: calc.mov,
        playerEloPre: calc.playerEloPre,
        // Newbie multipliers that were applied to this match — so that
        // the history view can show base numbers (without boost) in the team
        // pill and boosted numbers behind the player's 2x pill. Same pattern as
        // tournament matches (stored on match.t1NewbieMul/t2NewbieMul).
        t1NewbieMul: calc.t1NewbieMul,
        t2NewbieMul: calc.t2NewbieMul,
      },
    };

    // The SAME list the calculation above used (#242), so what was recorded is
    // what is applied. It also keeps an in-progress tournament's deltas out of
    // the player table until that tournament commits them.
    const result = applySingleMatchElo(allPlayers, match);
    if (result.error) {
      return { success: false, error: result.error };
    }

    // Save — build newList explicitly from current state (not from the closure)
    // and use functional setState to avoid race conditions
    const newList = [enrichedMatch, ...singleMatches];

    try {
      // Save to Storage FIRST (not setState yet, so we can roll back)
      await persistSingleMatches(newList);
      await persistPlayers(result.players);

      // After Storage is confirmed, update state
      setSingleMatches(newList);
      setAllPlayers(result.players);

      console.log('[Padel24] Lagret enkeltkamp, totalt:', newList.length, 'kamper');
      return { success: true, match: enrichedMatch, calc };
    } catch (e: any) {
      console.error('[Padel24] Kunne ikke lagre enkeltkamp:', e);
      return { success: false, error: e.message };
    }
  };

  // Delete a single match + reverse the ELO change
  const deleteSingleMatch = async (matchId: any) => {
    const match = singleMatches.find((m) => m.id === matchId);
    if (!match || !match.result) {
      return { success: false, error: 'Kampen ble ikke funnet' };
    }

    // Reverse the ELO on the players
    const reversedPlayers = allPlayers.map((p) => {
      if (p.id === match.team1.p1Id || p.id === match.team1.p2Id) {
        return { ...p, elo: p.elo - match.result.t1Change };
      }
      if (p.id === match.team2.p1Id || p.id === match.team2.p2Id) {
        return { ...p, elo: p.elo - match.result.t2Change };
      }
      return p;
    });

    const newList = singleMatches.filter((m) => m.id !== matchId);

    if (singleMatchesAreServerAuthoritative()) {
      try {
        await deleteSingleMatchCmd(matchId);
        const freshPlayers = await fetchPlayers();
        setSingleMatches(newList);
        setAllPlayers(freshPlayers);
        return { success: true };
      } catch (e: any) {
        console.error('[Padel24] Kunne ikke slette enkeltkamp:', e);
        return { success: false, error: e.message };
      }
    }

    try {
      await persistSingleMatches(newList);
      await persistPlayers(reversedPlayers);
      setSingleMatches(newList);
      setAllPlayers(reversedPlayers);
      console.log('[Padel24] Slettet enkeltkamp, gjenstår:', newList.length, 'kamper');
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e.message };
    }
  };

  // Update a single match — reverse the old ELO, apply the new
  const updateSingleMatch = async (
    matchId: any,
    { team1, team2, sets, format, kFactor, playedAt }: any,
  ) => {
    const oldMatch = singleMatches.find((m) => m.id === matchId);
    if (!oldMatch || !oldMatch.result) {
      return { success: false, error: 'Kampen ble ikke funnet' };
    }

    if (singleMatchesAreServerAuthoritative()) {
      try {
        const { match: saved, calc } = await patchSingleMatch(matchId, {
          team1,
          team2,
          sets,
          format,
          kFactor,
          playedAt,
        });
        const freshPlayers = await fetchPlayers();
        setSingleMatches(singleMatches.map((m) => (m.id === matchId ? saved : m)));
        setAllPlayers(freshPlayers);
        return { success: true, match: saved, calc };
      } catch (e: any) {
        console.error('[Padel24] Kunne ikke oppdatere enkeltkamp:', e);
        return { success: false, error: e.message };
      }
    }

    // Step 1: Reverse the old ELO
    const playersAfterReversal = allPlayers.map((p) => {
      if (p.id === oldMatch.team1.p1Id || p.id === oldMatch.team1.p2Id) {
        return { ...p, elo: p.elo - oldMatch.result.t1Change };
      }
      if (p.id === oldMatch.team2.p1Id || p.id === oldMatch.team2.p2Id) {
        return { ...p, elo: p.elo - oldMatch.result.t2Change };
      }
      return p;
    });

    // Step 2: Create a new match record (with the same ID to keep its position)
    // (eloWeightPct is omitted here — createSingleMatch derives kFactor and copes
    // with a missing eloWeightPct at runtime; `as any` keeps the verbatim-moved
    // call signature, since createSingleMatch is now typed with eloWeightPct required.)
    const newMatch = createSingleMatch({
      team1,
      team2,
      sets,
      format,
      kFactor,
      playedAt,
      registeredBy: oldMatch.registeredBy || 'admin',
    } as any);
    newMatch.id = matchId; // keep the ID

    // Step 3: Compute the new ELO based on the reversed players
    const calc = calcSingleMatchEloChange(playersAfterReversal, newMatch);
    if (calc.error) {
      return { success: false, error: calc.error };
    }

    const enrichedMatch = {
      ...newMatch,
      result: {
        winner: calc.winner,
        t1Games: calc.t1Games,
        t2Games: calc.t2Games,
        t1EloBefore: calc.t1EloBefore,
        t2EloBefore: calc.t2EloBefore,
        t1Change: calc.t1Change,
        t2Change: calc.t2Change,
        mov: calc.mov,
        playerEloPre: calc.playerEloPre,
        t1NewbieMul: calc.t1NewbieMul,
        t2NewbieMul: calc.t2NewbieMul,
      },
    };

    // Step 4: Apply the new ELO
    const result = applySingleMatchElo(playersAfterReversal, newMatch);
    if (result.error) {
      return { success: false, error: result.error };
    }

    // Step 5: Save
    const newList = singleMatches.map((m) => (m.id === matchId ? enrichedMatch : m));

    try {
      await persistSingleMatches(newList);
      await persistPlayers(result.players);
      setSingleMatches(newList);
      setAllPlayers(result.players);
      console.log('[Padel24] Oppdaterte enkeltkamp, totalt:', newList.length, 'kamper');
      return { success: true, match: enrichedMatch, calc };
    } catch (e: any) {
      console.error('[Padel24] Kunne ikke oppdatere enkeltkamp:', e);
      return { success: false, error: e.message };
    }
  };

  return { addSingleMatch, deleteSingleMatch, updateSingleMatch };
}
